import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import TrainingProgressRing from "@/components/TrainingProgressRing";
import {
  LogOut,
  LayoutDashboard,
  CreditCard,
  ShieldCheck,
  Settings as SettingsIcon,
  Users as UsersIcon,
  IdCard,
  Menu,
  X,
  GraduationCap,
} from "lucide-react";

const linkClass = ({ isActive }) =>
  `flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
    isActive
      ? "font-semibold text-slate-900"
      : "text-slate-900 hover:text-slate-700"
  }`;

const adminLinkClass = ({ isActive }) =>
  `flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
    isActive ? "text-blue-700" : "text-blue-700/70 hover:text-blue-700"
  }`;

function NavLinks({ user, onClick }) {
  if (!user) return null;
  return (
    <>
      <NavLink to="/dashboard" data-testid="nav-dashboard" className={linkClass} onClick={onClick}>
        <LayoutDashboard className="h-4 w-4" />
        Dashboard
      </NavLink>
      <NavLink to="/applicants" data-testid="nav-applicants" className={linkClass} onClick={onClick}>
        <IdCard className="h-4 w-4" />
        Applicants
      </NavLink>
      <NavLink to="/billing" data-testid="nav-billing" className={linkClass} onClick={onClick}>
        <CreditCard className="h-4 w-4" />
        Billing
      </NavLink>
      <NavLink to="/team" data-testid="nav-team" className={linkClass} onClick={onClick}>
        <UsersIcon className="h-4 w-4" />
        Team
      </NavLink>
      <NavLink to="/settings" data-testid="nav-settings" className={linkClass} onClick={onClick}>
        <SettingsIcon className="h-4 w-4" />
        Settings
      </NavLink>
      <NavLink to="/learn" data-testid="nav-learn" className={linkClass} onClick={onClick}>
        <GraduationCap className="h-4 w-4" />
        Learn
      </NavLink>
      {user.role === "admin" && (
        <NavLink to="/admin" data-testid="nav-admin" className={adminLinkClass} onClick={onClick}>
          <ShieldCheck className="h-4 w-4" />
          Admin
        </NavLink>
      )}
    </>
  );
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const closeSheet = () => setOpen(false);

  // Close mobile menu on route change (safety net — NavLink onClick covers most cases)
  if (open && typeof window !== "undefined") {
    // no-op; close handler attached below
  }

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 border-b border-slate-200 bg-white/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:h-20 sm:px-6">
        <Link
          to="/"
          data-testid="navbar-logo"
          aria-label="Hireloop PRO"
          className="group flex flex-none items-center gap-2 sm:gap-3"
          onClick={closeSheet}
        >
          <img
            src="/brand/hireloop-h.png"
            alt=""
            aria-hidden="true"
            className="h-9 w-9 flex-none object-contain sm:h-12 sm:w-12"
            style={{ mixBlendMode: "multiply" }}
          />
          <div className="relative h-9 w-32 overflow-hidden sm:h-12 sm:w-48">
            <img
              src="/brand/hireloop-wordmark.png"
              alt="Hireloop PRO"
              className="pointer-events-none absolute left-1/2 top-[-83px] h-36 w-auto sm:top-[-120px] sm:h-52"
              style={{
                transform: "translateX(-50%)",
                mixBlendMode: "multiply",
              }}
            />
          </div>
        </Link>

        {/* Desktop nav (≥ md) */}
        <nav className="hidden items-center gap-1 md:flex">
          <NavLinks user={user} />
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <div className="hidden md:block">
                <TrainingProgressRing />
              </div>
              <Link
                to="/settings"
                data-testid="navbar-user-name"
                className="hidden text-sm text-black hover:text-slate-700 md:block"
              >
                {user.name}
              </Link>
              <Button
                variant="ghost"
                size="sm"
                data-testid="navbar-logout-btn"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                className="hidden rounded-sm md:inline-flex"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
              {/* Mobile hamburger */}
              <button
                type="button"
                data-testid="navbar-mobile-toggle"
                aria-expanded={open}
                aria-label="Open menu"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 md:hidden"
              >
                {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="navbar-login-btn"
                onClick={() => navigate("/login")}
                className="rounded-sm"
              >
                Sign in
              </Button>
              <Button
                size="sm"
                data-testid="navbar-signup-btn"
                onClick={() => navigate("/register")}
                className="bg-brand-gradient rounded-sm text-white"
              >
                Get started
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Mobile menu sheet (slide-down panel) */}
      {user && open && (
        <div
          data-testid="navbar-mobile-sheet"
          className="border-t border-slate-200 bg-white md:hidden"
        >
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
            <div className="border-b border-slate-100 pb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Signed in as
            </div>
            <Link
              to="/settings"
              onClick={closeSheet}
              data-testid="navbar-mobile-user"
              className="border-b border-slate-100 pb-3 text-sm text-slate-900"
            >
              {user.name}{" "}
              <span className="ml-1 text-xs text-slate-500">· {user.email}</span>
            </Link>
            <NavLinks user={user} onClick={closeSheet} />
            <button
              type="button"
              data-testid="navbar-mobile-logout"
              onClick={() => {
                setOpen(false);
                logout();
                navigate("/");
              }}
              className="mt-2 flex items-center gap-2 border-t border-slate-100 px-3 pt-3 text-sm text-rose-700 hover:text-rose-900"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </nav>
        </div>
      )}
      {/* Close menu on route change */}
      {open && (
        <RouteListener path={location.pathname} onChange={closeSheet} />
      )}
    </header>
  );
}

// Tiny helper component that fires onChange when path changes.
// Kept inside this file to avoid adding a new module for one-line behaviour.
function RouteListener({ path, onChange }) {
  const [last, setLast] = useState(path);
  if (last !== path) {
    setLast(path);
    onChange();
  }
  return null;
}
